import { logger } from '@/core/logger';
import { getSdkClient, mapAppLocaleToContentful } from './contentfulClient';

type Locale = 'en' | 'ar';
type EntriesQueries = Record<string, any>;

export interface LegalDocument {
    name: string;
    content: any; // RichText format from Contentful
}

/**
 * Fetch legal document by name from Contentful
 * @param documentName - The name of the document (e.g., 'terms-and-conditions', 'privacy-policy')
 * @param locale - The locale to fetch ('en' or 'ar')
 * @returns The legal document or null if not found
 */
export async function fetchLegalDocument(
    documentName: string,
    locale: Locale = 'en'
): Promise<LegalDocument | null> {
    const client = getSdkClient();
    const cfLocale = mapAppLocaleToContentful(locale);

    try {
        const query: EntriesQueries = {
            content_type: 'legalDocuments',
            'fields.name': documentName,
            locale: cfLocale,
            limit: 1,
        };

        const data = await client.getEntries(query);
        const items = data.items || [];

        if (items.length === 0) {
            logger.info('legalDocumentsService', 'No legal document found', { documentName, locale: cfLocale });
            return null;
        }

        const entry = items[0];
        if (!entry) {
            logger.warn('legalDocumentsService', 'Entry is undefined', { documentName, locale: cfLocale });
            return null;
        }

        const fields = entry.fields || {};
        const nameField = fields.name;
        const name = typeof nameField === 'string' ? nameField : documentName;

        return {
            name,
            content: fields.content || null,
        };
    } catch (error) {
        logger.error('legalDocumentsService', 'Failed to fetch legal document', {
            documentName,
            locale: cfLocale,
            error: error instanceof Error ? error.message : String(error),
        });
        return null;
    }
}

/**
 * Extract plain text from Contentful RichText structure
 * @param richText - The RichText content from Contentful
 * @returns Plain text string
 */
export function extractTextFromRichText(richText: any): string {
    if (!richText) return '';

    let text = '';

    function traverse(node: any) {
        if (!node) return;

        if (node.nodeType === 'text' && node.value) {
            text += node.value;
        }

        if (node.content && Array.isArray(node.content)) {
            node.content.forEach((child: any) => {
                traverse(child);
            });
        }
    }

    traverse(richText);
    return text.trim();
}

/**
 * Convert Contentful RichText to HTML
 * @param richText - The RichText content from Contentful
 * @returns HTML string
 */
export function convertRichTextToHTML(richText: any): string {
    if (!richText) return '';

    function processNode(node: any): string {
        if (!node) return '';

        const nodeType = node.nodeType;

        switch (nodeType) {
            case 'document':
                if (node.content && Array.isArray(node.content)) {
                    return node.content.map(processNode).join('');
                }
                return '';

            case 'heading-1':
            case 'heading-2':
            case 'heading-3':
            case 'heading-4':
            case 'heading-5':
            case 'heading-6': {
                const level = parseInt(nodeType.split('-')[1]) || 1;
                const content = node.content && Array.isArray(node.content) 
                    ? node.content.map(processNode).join('') 
                    : '';
                return `<h${level}>${content}</h${level}>`;
            }

            case 'paragraph': {
                const content = node.content && Array.isArray(node.content) 
                    ? node.content.map(processNode).join('') 
                    : '';
                return `<p>${content}</p>`;
            }

            case 'text': {
                let text = node.value || '';
                const marks = node.marks || [];
                
                marks.forEach((mark: any) => {
                    switch (mark.type) {
                        case 'bold':
                            text = `<strong>${text}</strong>`;
                            break;
                        case 'italic':
                            text = `<em>${text}</em>`;
                            break;
                        case 'underline':
                            text = `<u>${text}</u>`;
                            break;
                        case 'code':
                            text = `<code>${text}</code>`;
                            break;
                    }
                });
                
                return text;
            }

            case 'hyperlink':
            case 'entry-hyperlink':
            case 'asset-hyperlink': {
                const url = node.data?.uri || node.data?.target?.sys?.uri || '';
                const content = node.content && Array.isArray(node.content) 
                    ? node.content.map(processNode).join('') 
                    : '';
                
                if (url && typeof url === 'string') {
                    return `<a href="${escapeHtml(url)}">${content}</a>`;
                }
                return content;
            }

            case 'unordered-list':
                const ulContent = node.content && Array.isArray(node.content) 
                    ? node.content.map(processNode).join('') 
                    : '';
                return `<ul>${ulContent}</ul>`;

            case 'ordered-list':
                const olContent = node.content && Array.isArray(node.content) 
                    ? node.content.map(processNode).join('') 
                    : '';
                return `<ol>${olContent}</ol>`;

            case 'list-item':
                const liContent = node.content && Array.isArray(node.content) 
                    ? node.content.map(processNode).join('') 
                    : '';
                return `<li>${liContent}</li>`;

            case 'blockquote':
                const blockquoteContent = node.content && Array.isArray(node.content) 
                    ? node.content.map(processNode).join('') 
                    : '';
                return `<blockquote>${blockquoteContent}</blockquote>`;

            case 'hr':
                return '<hr>';

            default:
                // For unknown node types, try to process content
                if (node.content && Array.isArray(node.content)) {
                    return node.content.map(processNode).join('');
                }
                return '';
        }
    }

    function escapeHtml(text: string): string {
        const map: Record<string, string> = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
        };
        return text.replace(/[&<>"']/g, (m) => map[m] || m);
    }

    if (richText.nodeType === 'document') {
        return processNode(richText);
    }

    // If it's not a document, wrap it
    return processNode({ nodeType: 'document', content: [richText] });
}

/**
 * Parse RichText into structured sections for rendering
 * @param richText - The RichText content from Contentful
 * @returns Array of sections with headings and paragraphs
 */
export interface RichTextInlineElement {
    type: 'text' | 'link';
    text: string;
    isBold?: boolean;
    url?: string; // For hyperlinks
}

export interface RichTextSection {
    type: 'heading' | 'paragraph';
    level?: number; // For headings (1-6)
    text?: string; // For simple headings or paragraphs without inline elements
    inlineElements?: RichTextInlineElement[]; // For paragraphs with mixed content (text + links)
    isBold?: boolean; // For simple paragraphs
}

export function parseRichTextToSections(richText: any): RichTextSection[] {
    if (!richText) return [];

    const sections: RichTextSection[] = [];

    function extractTextFromNode(node: any): { text: string; isBold: boolean } {
        let text = '';
        let isBold = false;

        if (node.nodeType === 'text') {
            const hasBold = node.marks?.some((mark: any) => mark.type === 'bold');
            if (hasBold) isBold = true;
            text = node.value || '';
        } else if (node.content && Array.isArray(node.content)) {
            node.content.forEach((child: any) => {
                const childResult = extractTextFromNode(child);
                text += childResult.text;
                if (childResult.isBold) isBold = true;
            });
        }

        return { text, isBold };
    }

    function traverse(node: any) {
        if (!node) return;

        if (node.nodeType === 'heading-1' || node.nodeType === 'heading-2' || 
            node.nodeType === 'heading-3' || node.nodeType === 'heading-4' ||
            node.nodeType === 'heading-5' || node.nodeType === 'heading-6') {
            const level = parseInt(node.nodeType.split('-')[1]) || 1;
            const { text: headingText } = extractTextFromNode(node);

            if (headingText) {
                sections.push({
                    type: 'heading',
                    level,
                    text: headingText,
                });
            }
        } else if (node.nodeType === 'paragraph') {
            // Process paragraph content, handling inline nodes like hyperlinks
            if (node.content && Array.isArray(node.content)) {
                const inlineElements: RichTextInlineElement[] = [];
                let hasLinks = false;

                node.content.forEach((child: any) => {
                    if (child.nodeType === 'text') {
                        const { text, isBold } = extractTextFromNode(child);
                        if (text) {
                            inlineElements.push({
                                type: 'text',
                                text,
                                isBold,
                            });
                        }
                    } else if (child.nodeType === 'hyperlink' || child.nodeType === 'entry-hyperlink' || child.nodeType === 'asset-hyperlink') {
                        // Extract text from hyperlink
                        const { text: linkText, isBold: linkIsBold } = extractTextFromNode(child);
                        const url = child.data?.uri || child.data?.target?.sys?.uri || '';
                        
                        if (linkText) {
                            hasLinks = true;
                            inlineElements.push({
                                type: 'link',
                                text: linkText,
                                url: url || linkText, // Use text as fallback if no URL (for email addresses)
                                isBold: linkIsBold,
                            });
                        }
                    } else {
                        // Handle other inline nodes (extract text)
                        const { text, isBold } = extractTextFromNode(child);
                        if (text) {
                            inlineElements.push({
                                type: 'text',
                                text,
                                isBold,
                            });
                        }
                    }
                });

                // Create paragraph section with inline elements
                if (inlineElements.length > 0) {
                    if (hasLinks) {
                        // Paragraph with links - use inlineElements
                        sections.push({
                            type: 'paragraph',
                            inlineElements,
                        });
                    } else {
                        // Simple paragraph without links - use text for backward compatibility
                        const combinedText = inlineElements.map(el => el.text).join('');
                        const hasBold = inlineElements.some(el => el.isBold);
                        sections.push({
                            type: 'paragraph',
                            text: combinedText,
                            isBold: hasBold,
                        });
                    }
                }
            }
        }

        // Continue traversing children
        if (node.content && Array.isArray(node.content)) {
            node.content.forEach((child: any) => {
                traverse(child);
            });
        }
    }

    traverse(richText);
    return sections;
}

/**
 * Fetch current published version of Terms and Conditions from Contentful
 * Uses the same SDK pattern as fetchLegalDocument
 * @returns The published version number or null if not found
 */
export async function fetchTNCVersion(): Promise<number | null> {
    const client = getSdkClient();

    if (!client) {
        logger.warn('legalDocumentsService', 'SDK client not available, skipping TNC version fetch');
        return null;
    }

    try {
        const query: EntriesQueries = {
            content_type: 'legalDocuments',
            'fields.name': 'terms-and-conditions',
            limit: 1,
        };

        const data = await client.getEntries(query);
        const items = data.items || [];

        if (items.length === 0) {
            logger.info('legalDocumentsService', 'No TNC document found');
            return null;
        }

        const entry = items[0];
        if (!entry) {
            logger.warn('legalDocumentsService', 'TNC entry is undefined');
            return null;
        }

        // Access publishedVersion from entry.sys.publishedVersion
        const version = entry.sys?.publishedVersion;

        if (version !== undefined && version !== null) {
            logger.info('legalDocumentsService', 'TNC version fetched successfully', {
                version,
                entryId: entry.sys?.id,
            });
            return version;
        } else {
            logger.warn('legalDocumentsService', 'TNC version not found in entry', {
                entryId: entry.sys?.id,
            });
            return null;
        }
    } catch (error) {
        logger.error('legalDocumentsService', 'Failed to fetch TNC version', {
            error: error instanceof Error ? error.message : String(error),
        });
        return null;
    }
}

